import { inject, Injectable } from '@angular/core';
import { AmplitudeService } from '@brighthub/logging';
import { CarePlanCaseStatus } from '../care-plan.model';

/**
 * Amplitude events for Care Plan (PRD §9), wired from successful local
 * store mutations (D8) — provisional; retained in the approved dev/test
 * analytics environment for continuous validation, not evidence of a
 * durable production record. Analytics/CI must review before production
 * rollout.
 *
 * PPI: ids / counts / statuses only — never case descriptions or goal text.
 *
 * Deliberately omitted per PRD §9, not invented as empty/false values:
 * - `case_opened.ai_suggested` / `.ai_overridden` / `.case_source` — Navi's
 *   read is deferred by Jira scope (D6); this slice has no AI signal to report.
 * - `case_status_changed.actor_role` — no role/session data source exists
 *   in this slice to populate it truthfully.
 * - `case_duplicate_warning_shown.fa_decision` — fired at the moment the
 *   warning appears, before the FA has decided.
 */
@Injectable({ providedIn: 'root' })
export class CarePlanAnalytics {
  private readonly _amplitude = inject(AmplitudeService);

  caseOpened(props: {
    caseId: string;
    domain: string;
    objectiveType: string;
    initialStatus: CarePlanCaseStatus;
    linkedGoalIds: string[];
  }): void {
    this._amplitude.logEvent('case_opened', {
      case_id: props.caseId,
      domain: props.domain,
      objective_type: props.objectiveType,
      initial_status: props.initialStatus,
      linked_goal_ids: props.linkedGoalIds,
      app: 'brightnav',
    });
  }

  caseStatusChanged(
    caseId: string,
    fromStatus: CarePlanCaseStatus,
    toStatus: CarePlanCaseStatus,
  ): void {
    this._amplitude.logEvent('case_status_changed', {
      case_id: caseId,
      from_status: fromStatus,
      to_status: toStatus,
      app: 'brightnav',
    });
  }

  caseClosed(
    caseId: string,
    closureReason: string,
    hasClosingNote: boolean,
  ): void {
    this._amplitude.logEvent('case_closed', {
      case_id: caseId,
      closure_reason: closureReason,
      has_closing_note: hasClosingNote,
      app: 'brightnav',
    });
  }

  caseGoalLinked(
    caseId: string,
    goalId: string,
    clientGuid: string | null,
  ): void {
    this._amplitude.logEvent('case_goal_linked', {
      client_guid: clientGuid,
      case_id: caseId,
      goal_id: goalId,
      app: 'brightnav',
    });
  }

  caseGoalUnlinked(
    caseId: string,
    goalId: string,
    clientGuid: string | null,
  ): void {
    this._amplitude.logEvent('case_goal_unlinked', {
      client_guid: clientGuid,
      case_id: caseId,
      goal_id: goalId,
      app: 'brightnav',
    });
  }

  caseDuplicateWarningShown(domain: string, objectiveType: string): void {
    this._amplitude.logEvent('case_duplicate_warning_shown', {
      domain,
      objective_type: objectiveType,
      app: 'brightnav',
    });
  }

  goalCreated(
    goalId: string,
    activeGoalCount: number,
    clientGuid: string | null,
  ): void {
    this._amplitude.logEvent('goal_created', {
      client_guid: clientGuid,
      goal_id: goalId,
      active_goal_count: activeGoalCount,
      app: 'brightnav',
    });
  }

  goalCapBlocked(clientGuid: string | null): void {
    this._amplitude.logEvent('goal_cap_blocked', {
      client_guid: clientGuid,
      app: 'brightnav',
    });
  }

  goalEdited(goalId: string, clientGuid: string | null): void {
    this._amplitude.logEvent('goal_edited', {
      client_guid: clientGuid,
      goal_id: goalId,
      app: 'brightnav',
    });
  }

  goalCompleted(
    goalId: string,
    linkedCaseCount: number,
    clientGuid: string | null,
  ): void {
    this._amplitude.logEvent('goal_completed', {
      client_guid: clientGuid,
      goal_id: goalId,
      linked_case_count: linkedCaseCount,
      app: 'brightnav',
    });
  }

  goalRemoved(
    goalId: string,
    linkedCaseCount: number,
    clientGuid: string | null,
  ): void {
    this._amplitude.logEvent('goal_removed', {
      client_guid: clientGuid,
      goal_id: goalId,
      linked_case_count: linkedCaseCount,
      app: 'brightnav',
    });
  }

  optionCreated(props: {
    clientGuid: string;
    caseId: string;
    optionId: string;
    optionType: string;
    initialStatus: string;
    sourceType: string | null;
  }): void {
    this._amplitude.logEvent('option_created', {
      client_guid: props.clientGuid,
      case_id: props.caseId,
      option_id: props.optionId,
      option_type: props.optionType,
      initial_status: props.initialStatus,
      source_type: props.sourceType,
      app: 'brightnav',
    });
  }

  optionStatusChanged(props: {
    clientGuid: string;
    caseId: string;
    optionId: string;
    optionType: string;
    fromStatus: string;
    toStatus: string;
  }): void {
    this._amplitude.logEvent('option_status_changed', {
      client_guid: props.clientGuid,
      case_id: props.caseId,
      option_id: props.optionId,
      option_type: props.optionType,
      from_status: props.fromStatus,
      to_status: props.toStatus,
      app: 'brightnav',
    });
  }

  actionCreateStarted(props: { clientGuid: string; caseId: string }): void {
    this._amplitude.logEvent('action_create_started', {
      client_guid: props.clientGuid,
      case_id: props.caseId,
      entry_point: 'case',
      app: 'brightnav',
    });
  }

  actionCreated(props: {
    clientGuid: string;
    actionId: string;
    caseId: string;
    actionType: string | null;
    priority: string | null;
    hasDueDate: boolean;
    clientPromise: boolean;
    ownerId: string | null;
  }): void {
    this._amplitude.logEvent('action_created', {
      client_guid: props.clientGuid,
      action_id: props.actionId,
      case_id: props.caseId,
      action_type: props.actionType,
      priority: props.priority,
      has_due_date: props.hasDueDate,
      client_promise: props.clientPromise,
      owner_id: props.ownerId,
      app: 'brightnav',
    });
  }

  actionStatusChanged(props: {
    clientGuid: string;
    caseId: string;
    actionId: string;
    actionType: string | null;
    fromStatus: string | null;
    toStatus: string | null;
  }): void {
    this._amplitude.logEvent('action_status_changed', {
      client_guid: props.clientGuid,
      case_id: props.caseId,
      action_id: props.actionId,
      action_type: props.actionType,
      from_status: props.fromStatus,
      to_status: props.toStatus,
      app: 'brightnav',
    });
  }

  actionReassigned(props: {
    clientGuid: string;
    caseId: string;
    actionId: string;
    actionType: string | null;
    fromOwnerId: string | null;
    toOwnerId: string | null;
  }): void {
    this._amplitude.logEvent('action_reassigned', {
      client_guid: props.clientGuid,
      case_id: props.caseId,
      action_id: props.actionId,
      action_type: props.actionType,
      from_owner_id: props.fromOwnerId,
      to_owner_id: props.toOwnerId,
      app: 'brightnav',
    });
  }
}
